import { documentApi } from 'src/api/document';
import { profileApi } from 'src/api/profile';
import { visitApi } from 'src/api/visit';
import { publicVisitApi } from 'src/api/visit-public';

export type FileOwner =
  | { kind: 'document'; documentId: string }
  | { kind: 'profileFile'; profileId: string; filename: string }
  | { kind: 'visitFile'; visitId: string; filename: string }
  | {
      kind: 'guestVisitFile';
      visitId: string;
      filename: string;
      /** Guest session id sent as X-Guest-Session; the endpoint is session-gated. */
      sessionId?: string;
    };

export const getFileUrl = async (owner: FileOwner): Promise<string> => {
  switch (owner.kind) {
    case 'document': {
      const { data } = await documentApi.documentControllerGetDocumentSignedUrl({
        documentId: owner.documentId,
      });
      if (!data.url) throw new Error('No signed URL returned for document');
      return data.url;
    }
    case 'profileFile': {
      const { data } = await profileApi.profileControllerGetProfileFileSignedUrl({
        profileId: owner.profileId,
        filename: owner.filename,
      });
      if (!data.url) throw new Error('No signed URL returned for profileFile');
      return data.url;
    }
    case 'visitFile': {
      const { data } = await visitApi.visitControllerGetVisitFileSignedUrl({
        visitId: owner.visitId,
        filename: owner.filename,
      });
      if (!data.url) throw new Error('No signed URL returned for visitFile');
      return data.url;
    }
    case 'guestVisitFile': {
      // The guest endpoint is session-gated (GuestSessionGuard reads the
      // x-guest-session header). The shared axios client only attaches the
      // Firebase token, so the guest session must be passed explicitly here —
      // otherwise the request 401s and the document never opens.
      const { data } = await publicVisitApi.visitGuestAttachmentControllerGetSignedUrl(
        { visitId: owner.visitId, filename: owner.filename },
        owner.sessionId
          ? { headers: { 'X-Guest-Session': owner.sessionId } }
          : undefined,
      );
      if (!data.url) throw new Error('No signed URL returned for guestVisitFile');
      return data.url;
    }
  }
};
